/**
 * Which chart cells should be in memory right now.
 *
 * The app used to load every cell in the manifest at startup and keep them all.
 * That is fine for ten small training cells and impossible for a real chart set:
 * NOAA alone is 7,133 cells, and a single band-3 Alaskan cell is 25 MB of
 * GeoJSON on its own. So cells become resident on demand.
 *
 * The part that needs care is what "on demand" means. Loading only what is on
 * screen would be wrong, because two things read the chart without looking at
 * it. The depth alarm and the danger scan work off the water under the boat, and
 * the route check works along the whole planned route. If either of those ran
 * against a cell that happened to be off screen, it would find nothing and say
 * nothing, which is the worst possible failure for a safety check: silence that
 * looks like an all-clear.
 *
 * So the resident set is the union of three things, in priority order:
 *
 *   ship   the cells around own ship, so the depth and danger alarms always have
 *          chart under them, at the most detailed scale available
 *   route  the cells along the active route, so the hazard scan sees the whole
 *          passage rather than the visible part of it
 *   view   the cells under the viewport, at the scale band suited to the zoom
 *   held   already-loaded cells nothing claims right now, kept while the cap
 *          has room. Eviction is LAZY: losing your reason does not cost you
 *          your slot, it costs you the slot as soon as a wanted cell needs it.
 *
 * Held exists because the eager alternative was measured and it thrashes. The
 * tracking recentre (or any programmatic bounce) can put the view somewhere else
 * for a single residency pass, and the eager plan evicted every viewport cell on
 * that pass, megabytes fetched moments earlier, then re-fetched the lot when the
 * next pass came home. On a phone over mobile data that read as the chart
 * randomly thinning and refilling with every gesture. A resident cell costs
 * nothing to keep (the cap already bounds memory), so the only sensible moment
 * to drop one is when a wanted cell needs its slot.
 *
 * Ship and route come first because they are safety, not drawing. If the cap is
 * reached, it is the view that loses cells, and the worst that does is leave a
 * gap on screen.
 *
 * With one limit. "The view loses" used to mean the view could lose EVERYTHING.
 * Sail the boat somewhere densely charted, pan the map somewhere else, and the
 * ship cells alone could fill all twelve slots, so the place actually on screen
 * drew almost nothing, and got worse as you zoomed in. That is how it showed up
 * in the field: detail over Bahia de Guayanilla vanishing between the 0.7 nm and
 * 0.3 nm scales while the simulated boat sat off Brittany, four thousand miles
 * from the viewport. So the view now keeps a small reserved share of the cap.
 * The ship still wins almost all of a contested cap, and what it gives up is its
 * farthest cells, not the water under the keel.
 */
import { boundsIntersect } from '@/chart-sources/providers';

export type ResidencyReason = 'ship' | 'route' | 'view' | 'held';

export type Bounds = [number, number, number, number];

export interface ResidencyCandidate {
  id: string;
  /** Which ChartSource the cell came from, so the loader knows who to ask. */
  sourceId: string;
  /** [west, south, east, north] in WGS84 degrees. */
  bounds: Bounds;
  /** S-57 navigational purpose, 1 overview to 6 berthing. */
  scaleBand: number;
  /**
   * Roughly how heavy this cell is to hold, in features. The manifest's
   * featureCount when it has one, undefined when it does not.
   *
   * A cell COUNT is a poor proxy for memory and always was: twelve Scottish
   * training cells are a few thousand features, while twelve dense NOAA cells
   * are millions, and the second set is what took the renderer out every time
   * Edd panned to America. See the weight budget below.
   */
  weight?: number;
}

export interface ResidencyOptions {
  candidates: ResidencyCandidate[];
  /** Current viewport, or null before the map has settled. */
  view?: Bounds | null;
  zoom: number;
  ownship?: [number, number] | null;
  /**
   * Waypoints of every route worth keeping chart under, each as its own list.
   * Kept separate rather than flattened so two unrelated routes do not imply a
   * leg between them.
   */
  routes?: [number, number][][];
  /** What is resident now, used to stop cells flickering in and out on a pan. */
  current?: Iterable<string>;
  maxCells?: number;
  /** Total feature budget for the resident set. See DEFAULT_MAX_FEATURES. */
  maxWeight?: number;
}

export interface ResidencyPlan {
  /** Cells that should be resident, highest priority first. */
  keep: ResidencyCandidate[];
  reasons: Map<string, ResidencyReason>;
  /** Cells to load, not resident yet. */
  add: string[];
  /** Cells to drop. */
  remove: string[];
  /** How many wanted cells the cap turned away. Worth logging, never silent. */
  cappedOut: number;
  /** How many cells the FEATURE budget turned away, and the kept weight. */
  weightTrimmed: number;
  keptWeight: number;
}

/**
 * How many cells we are willing to hold. Twelve covers a viewport and its
 * neighbours at any sensible zoom while keeping the parsed feature count in the
 * tens of thousands rather than the millions.
 */
export const DEFAULT_MAX_CELLS = 12;

/**
 * The resident FEATURE budget, which is the cap that actually matters.
 *
 * Twelve cells was the only limit for months, and it is a limit on the wrong
 * quantity: it says nothing about how big those cells are. Off Mull twelve
 * cells is a few thousand features; off the American coast, where the ENC set
 * is dense and a single band-3 cell can be 25 MB of GeoJSON, twelve cells is
 * millions of features and several hundred megabytes, and Android answers that
 * by taking the renderer away. From the deck that is "the app crashes every
 * time I pan to America" (fb-msp4c0i6-0).
 *
 * 120,000 features holds a working chart at any sensible zoom while leaving
 * the WebView room to draw it. A cell with no stated featureCount is assumed
 * to be ASSUMED_CELL_WEIGHT, chosen so that twelve unknown cells sit just
 * inside the budget: on a chart set that publishes no counts (the training
 * cells) nothing changes at all, and the budget only bites where cells are
 * genuinely heavy.
 */
export const DEFAULT_MAX_FEATURES = 120_000;

/** What a cell weighs when its manifest does not say. */
export const ASSUMED_CELL_WEIGHT = 8_000;

/** Never trim below this many cells, however heavy: a blank chart helps nobody. */
export const MIN_CELLS_KEPT = 2;

/**
 * How many of the capped slots the view is guaranteed when ship and route want
 * more than everything. Three is enough to draw the detail cell under the view
 * centre and its neighbours, which is the difference between "the chart thins a
 * little" and "the harbour you are looking at is blank". The safety pool keeps
 * the rest, and its cells are dropped farthest-from-the-boat first, so the water
 * under the keel is untouched.
 */
export const VIEW_RESERVED_CELLS = 3;

/** Own ship keeps chart under it out to here, so the alarms never run dry. */
export const SHIP_RADIUS_NM = 6;

/** The route corridor, wide enough to cover a cross-track excursion. */
export const ROUTE_PAD_NM = 2;

/** Load a little beyond the screen so a slow pan does not stutter. */
const VIEW_PAD = 0.25;

/** A resident cell holds on further out than that, so panning does not thrash. */
const VIEW_KEEP_PAD = 0.6;

/**
 * The scale band a zoom level wants. S-57 bands run 1 (overview) to 6
 * (berthing); these thresholds put band 5 on the harbour-approach zooms where
 * most sailing happens.
 */
export function preferredBand(zoom: number): number {
  if (zoom < 5.5) return 1;
  if (zoom < 7.5) return 2;
  if (zoom < 9.5) return 3;
  if (zoom < 11.5) return 4;
  if (zoom < 14) return 5;
  return 6;
}

function padBounds(b: Bounds, fraction: number): Bounds {
  const dx = (b[2] - b[0]) * fraction;
  const dy = (b[3] - b[1]) * fraction;
  return [b[0] - dx, b[1] - dy, b[2] + dx, b[3] + dy];
}

/** A bounding box a given number of nautical miles around a position. */
export function boundsAround(position: [number, number], nm: number): Bounds {
  const [lon, lat] = position;
  const dLat = nm / 60;
  // One minute of longitude shrinks with the cosine of latitude. Clamped so a
  // position near the pole cannot produce an infinite box.
  const cos = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
  const dLon = nm / 60 / cos;
  return [lon - dLon, lat - dLat, lon + dLon, lat + dLat];
}

/** The corridor around a route: each leg's box, padded. */
export function routeCorridor(points: [number, number][], nm: number): Bounds[] {
  if (points.length === 0) return [];
  if (points.length === 1) return [boundsAround(points[0], nm)];
  const out: Bounds[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const [aLon, aLat] = points[i];
    const [bLon, bLat] = points[i + 1];
    const midLat = (aLat + bLat) / 2;
    const cos = Math.max(Math.cos((midLat * Math.PI) / 180), 0.01);
    const dLat = nm / 60;
    const dLon = nm / 60 / cos;
    out.push([
      Math.min(aLon, bLon) - dLon,
      Math.min(aLat, bLat) - dLat,
      Math.max(aLon, bLon) + dLon,
      Math.max(aLat, bLat) + dLat,
    ]);
  }
  return out;
}

function centre(b: Bounds): [number, number] {
  return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
}

function distanceSq(a: [number, number], b: [number, number]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

/**
 * Keep the most detailed cells available for a safety match, plus one band back
 * as a fallback. Around the boat we want the largest scale the chart set offers,
 * not the overview that happens to cover the same water.
 */
function bestBands(matched: ResidencyCandidate[]): ResidencyCandidate[] {
  if (matched.length === 0) return [];
  const maxBand = Math.max(...matched.map((c) => c.scaleBand));
  return matched.filter((c) => c.scaleBand >= maxBand - 1);
}

/** Work out which cells should be in memory, and what to load and drop to get there. */
export function planResidency(opts: ResidencyOptions): ResidencyPlan {
  const {
    candidates,
    view = null,
    zoom,
    ownship = null,
    routes = [],
    maxCells = DEFAULT_MAX_CELLS,
    maxWeight = DEFAULT_MAX_FEATURES,
  } = opts;
  const current = new Set(opts.current ?? []);

  const reasons = new Map<string, ResidencyReason>();
  const ordered: ResidencyCandidate[] = [];
  const taken = new Set<string>();
  let cappedOut = 0;

  const push = (cell: ResidencyCandidate, reason: ResidencyReason): void => {
    if (taken.has(cell.id)) return;
    taken.add(cell.id);
    reasons.set(cell.id, reason);
    ordered.push(cell);
  };

  // 1. Around the boat. Safety first, and never band-filtered by zoom: the depth
  //    alarm cares what is under the keel, not what the screen is showing.
  if (ownship) {
    const box = boundsAround(ownship, SHIP_RADIUS_NM);
    const matched = candidates.filter((c) => boundsIntersect(c.bounds, box));
    const shipCentre = ownship;
    bestBands(matched)
      .sort((a, b) => distanceSq(centre(a.bounds), shipCentre) - distanceSq(centre(b.bounds), shipCentre))
      .forEach((c) => push(c, 'ship'));
  }

  // 2. Along the route, so the hazard scan sees the whole passage.
  if (routes.length > 0) {
    const corridor = routes.flatMap((points) => routeCorridor(points, ROUTE_PAD_NM));
    const matched = candidates.filter((c) => corridor.some((box) => boundsIntersect(c.bounds, box)));
    bestBands(matched).forEach((c) => push(c, 'route'));
  }

  // 3. What is on screen, at the band the zoom is asking for. A cell that is
  //    already resident holds on further out, so a pan does not drop and refetch
  //    the cell you are panning along.
  //
  //    Deliberately NOT here: clamping `want` to the finest band present in the
  //    view. It was proposed while chasing the vanishing-detail bug, and it is a
  //    provable no-op: when want sits above every available band, |band - want|
  //    is the available ordering plus a constant, so clamping cannot re-rank
  //    anything (checked across all band combinations). The finest cell on
  //    screen already always ranks first here. The vanish was the cap, below.
  if (view) {
    const wanted = padBounds(view, VIEW_PAD);
    const holdOn = padBounds(view, VIEW_KEEP_PAD);
    const want = preferredBand(zoom);
    const viewCentre = centre(view);
    candidates
      .filter(
        (c) =>
          boundsIntersect(c.bounds, wanted) ||
          (current.has(c.id) && boundsIntersect(c.bounds, holdOn)),
      )
      .sort((a, b) => {
        const band = Math.abs(a.scaleBand - want) - Math.abs(b.scaleBand - want);
        if (band !== 0) return band;
        return distanceSq(centre(a.bounds), viewCentre) - distanceSq(centre(b.bounds), viewCentre);
      })
      .forEach((c) => push(c, 'view'));
  }

  // Already-resident cells nothing claimed above ride along while there is room,
  // lowest priority, so under cap pressure they are the first out. Order within
  // them does not matter: all equally unclaimed, trimmed from the tail either way.
  for (const id of current) {
    if (taken.has(id)) continue;
    const cell = candidates.find((c) => c.id === id);
    // A resident cell absent from the candidates has lost its source (its pack
    // was deleted or switched off), so it genuinely must go.
    if (cell) push(cell, 'held');
  }

  // Nothing to go on yet (no view, no boat, no route): fall back to the cells
  // whose band suits the zoom, so a cold start still draws something.
  if (ordered.length === 0) {
    const want = preferredBand(zoom);
    [...candidates]
      .sort((a, b) => Math.abs(a.scaleBand - want) - Math.abs(b.scaleBand - want))
      .forEach((c) => push(c, 'view'));
  }

  let keep = ordered;
  if (keep.length > maxCells) {
    const wanted = ordered.filter((c) => reasons.get(c.id) !== 'held');
    const held = ordered.filter((c) => reasons.get(c.id) === 'held');
    if (wanted.length <= maxCells) {
      // The wanted cells all fit; passengers keep whatever room is left. Their
      // going is housekeeping, not a capacity complaint, so cappedOut stays 0.
      keep = [...wanted, ...held.slice(0, maxCells - wanted.length)];
    } else {
      // Genuinely contested: every passenger goes, and the wanted cells split
      // the cap. Ship and route still outrank the view, but the view keeps a
      // small reserve (never more than a quarter of the cap; at tiny caps that
      // is zero and safety wins outright). Both sides shed their most
      // expendable cells, because both lists are already sorted: the ship drops
      // its farthest cells, never the water under the keel, and the view keeps
      // its best-band, nearest-centre cells.
      cappedOut = wanted.length - maxCells;
      const safety = wanted.filter((c) => reasons.get(c.id) !== 'view');
      const viewCells = wanted.filter((c) => reasons.get(c.id) === 'view');
      const reserve = Math.min(VIEW_RESERVED_CELLS, viewCells.length, Math.floor(maxCells / 4));
      const safetyKept = safety.slice(0, Math.max(maxCells - reserve, 0));
      keep = [...safetyKept, ...viewCells.slice(0, maxCells - safetyKept.length)];
    }
    const keptIds = new Set(keep.map((c) => c.id));
    for (const cell of ordered) if (!keptIds.has(cell.id)) reasons.delete(cell.id);
  }

  // The weight budget, applied after the count cap and in the same priority
  // order, so what survives a heavy chart set is what the boat needs: the water
  // under the keel first, then the route, then the screen. A cell whose own
  // weight exceeds the whole budget is still kept if it is first in line, since
  // refusing to draw the only chart under the boat is worse than a hard pass at
  // memory; MIN_CELLS_KEPT is the floor for everything else.
  let weightTrimmed = 0;
  let keptWeight = 0;
  {
    const withinBudget: ResidencyCandidate[] = [];
    for (const cell of keep) {
      const w = cell.weight ?? ASSUMED_CELL_WEIGHT;
      const wouldBe = keptWeight + w;
      if (withinBudget.length >= MIN_CELLS_KEPT && wouldBe > maxWeight) {
        weightTrimmed += 1;
        reasons.delete(cell.id);
        continue;
      }
      withinBudget.push(cell);
      keptWeight = wouldBe;
    }
    keep = withinBudget;
  }

  const keepIds = new Set(keep.map((c) => c.id));
  return {
    keep,
    reasons,
    add: keep.filter((c) => !current.has(c.id)).map((c) => c.id),
    remove: [...current].filter((id) => !keepIds.has(id)),
    cappedOut,
    weightTrimmed,
    keptWeight,
  };
}
